import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Music2, MapPin, Search, Filter, Users, ExternalLink, Instagram, Facebook, Youtube, Globe } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { API_URL } from '../utils/api';

export default function ArtistsPage() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [performanceTypes, setPerformanceTypes] = useState([]);
  const [genres, setGenres] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');

  useEffect(() => {
    fetchArtists();
    fetchFilters();
  }, []);

  const fetchArtists = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedType) params.append('performance_type', selectedType);
      if (selectedGenre) params.append('genre', selectedGenre);

      const response = await fetch(`${API_URL}/api/artists?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setArtists(data);
      }
    } catch (error) {
      console.error('Error fetching artists:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [typesRes, genresRes] = await Promise.all([
        fetch(`${API_URL}/api/artists/performance-types`),
        fetch(`${API_URL}/api/artists/genres`)
      ]);
      
      if (typesRes.ok) {
        const types = await typesRes.json();
        setPerformanceTypes(types);
      }
      if (genresRes.ok) {
        const genresList = await genresRes.json();
        setGenres(genresList);
      }
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchArtists();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedType, selectedGenre]);

  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'instagram': return <Instagram className="w-4 h-4" />;
      case 'facebook': return <Facebook className="w-4 h-4" />;
      case 'youtube': return <Youtube className="w-4 h-4" />;
      default: return <Globe className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Music2 className="w-10 h-10" />
            <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Artists & Performers</h1>
          </div>
          <p className="text-violet-100 text-lg max-w-2xl">
            Discover talented performers for your line dance events. Browse our directory of singers, bands, and live entertainers.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                placeholder="Search artists..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-stone-200 rounded-xl"
                data-testid="artist-search-input"
              />
            </div>
            
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-2 border border-stone-200 rounded-xl bg-white text-stone-700"
              data-testid="artist-type-filter"
            >
              <option value="">All Performance Types</option>
              {performanceTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            <select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
              className="px-4 py-2 border border-stone-200 rounded-xl bg-white text-stone-700"
              data-testid="artist-genre-filter"
            >
              <option value="">All Genres</option>
              {genres.map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-stone-600">
            {loading ? 'Loading...' : `${artists.length} artist${artists.length !== 1 ? 's' : ''} found`}
          </p>
        </div>

        {/* Artists Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="overflow-hidden rounded-2xl">
                <Skeleton className="h-48 w-full" />
                <CardContent className="p-4">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2 mb-4" />
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : artists.length === 0 ? (
          <div className="text-center py-16">
            <Music2 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-700 mb-2">No Artists Found</h3>
            <p className="text-stone-500">
              {searchTerm || selectedType || selectedGenre 
                ? 'Try adjusting your search filters'
                : 'Be the first to register as an artist!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {artists.map(artist => (
              <Link
                key={artist.artist_id}
                to={`/artists/${artist.slug}`}
                className="group"
                data-testid={`artist-card-${artist.artist_id}`}
              >
                <Card className="overflow-hidden h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-stone-200 bg-white rounded-2xl">
                  {/* Image */}
                  <div className="relative h-48 bg-gradient-to-br from-violet-100 to-purple-50">
                    {artist.image_url ? (
                      <img
                        src={artist.image_url}
                        alt={artist.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music2 className="w-16 h-16 text-violet-300" />
                      </div>
                    )}
                    {artist.is_featured && (
                      <Badge className="absolute top-3 right-3 bg-amber-100 text-amber-700 border-0 rounded-full">
                        Featured
                      </Badge>
                    )}
                  </div>

                  <CardContent className="p-5">
                    {/* Name */}
                    <h3 className="text-xl font-bold text-stone-800 mb-1 group-hover:text-violet-600 transition-colors">
                      {artist.stage_name || artist.name}
                    </h3>
                    {artist.stage_name && artist.name !== artist.stage_name && (
                      <p className="text-sm text-stone-500 mb-2">{artist.name}</p>
                    )}

                    {/* Location */}
                    <div className="flex items-center text-stone-500 mb-3">
                      <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span className="text-sm truncate">{artist.location || 'Location not specified'}</span>
                    </div>

                    {/* Performance Types */}
                    {artist.performance_types?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {artist.performance_types.slice(0, 3).map(type => (
                          <Badge key={type} className="text-xs bg-violet-100 text-violet-700 border-0 rounded-full">
                            {type}
                          </Badge>
                        ))}
                        {artist.performance_types.length > 3 && (
                          <Badge variant="outline" className="text-xs border-stone-300 text-stone-500 rounded-full">
                            +{artist.performance_types.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Bio excerpt */}
                    <p className="text-sm text-stone-600 line-clamp-2">
                      {artist.bio}
                    </p>

                    {/* Social Links */}
                    {Object.keys(artist.social_links || {}).length > 0 && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-stone-100">
                        {Object.entries(artist.social_links).slice(0, 4).map(([platform, url]) => (
                          <a
                            key={platform}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-stone-400 hover:text-violet-600 transition-colors"
                          >
                            {getSocialIcon(platform)}
                          </a>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
